import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { cn } from "@/lib/utils";

interface PageProps {
  title: React.ReactNode;
  /** Optional eyebrow/kicker line rendered above the title (e.g. a finding's
   * severity · type · status). Use for domain-specific context that needs
   * consistent placement across detail pages. */
  eyebrow?: React.ReactNode;
  /** Optional left-edge accent (e.g. a FindingPolarityMark). Stretches to
   * span the eyebrow+title+description block. */
  leftMark?: React.ReactNode;
  description?: React.ReactNode;
  descriptionClassName?: string;
  back?: { to: string; label: string };
  actions?: React.ReactNode;
  maxWidth?: string;
  children: React.ReactNode;
}

export function Page({
  title,
  eyebrow,
  leftMark,
  description,
  descriptionClassName,
  back,
  actions,
  maxWidth,
  children,
}: PageProps) {
  return (
    <div
      className={cn(
        "min-w-0 space-y-6 px-4 py-6 pb-20 sm:px-6 lg:px-8",
        maxWidth && `${maxWidth} mx-auto`,
      )}
    >
      <div className="print:hidden">
        {back && (
          <Link
            to={back.to}
            className="mb-2.5 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="h-3 w-3" />
            {back.label}
          </Link>
        )}
        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 pb-1">
          <div className="flex min-w-0 flex-1 basis-[min(100%,520px)] items-stretch gap-3.5">
            {leftMark}
            <div className="min-w-0 flex-1">
              {eyebrow && <div className="mb-1.5">{eyebrow}</div>}
              <h1 className="text-xl font-semibold tracking-tight text-foreground">
                {title}
              </h1>
              {description && (
                <p
                  className={cn(
                    "mt-1 text-sm text-muted-foreground",
                    descriptionClassName,
                  )}
                >
                  {description}
                </p>
              )}
            </div>
          </div>
          {actions && (
            <div className="min-w-0 max-w-full shrink-0">{actions}</div>
          )}
        </div>
      </div>
      {children}
    </div>
  );
}
